/**
 * 커넥터 API 클라이언트
 */

import apiClient from './client';

export interface GmailConnectorConfig {
  connectorId: string;
  clientId?: string;
  clientSecret?: string;
  connected: boolean;
  connectedEmail?: string;
  updatedAt?: string;
}

export const connectorApi = {
  /** Gmail Client ID/Secret 저장 */
  async saveGmailConfig(clientId: string, clientSecret: string): Promise<GmailConnectorConfig> {
    const res = await apiClient.post<{ success: boolean; data: GmailConnectorConfig }>(
      '/api/connectors/gmail/config',
      { clientId, clientSecret }
    );
    return res.data.data;
  },

  /** Gmail 설정 조회 */
  async getGmailConfig(): Promise<GmailConnectorConfig | null> {
    const res = await apiClient.get<{ success: boolean; data: GmailConnectorConfig | null }>(
      '/api/connectors/gmail/config'
    );
    return res.data.data;
  },

  /** OAuth 인증 URL 조회 */
  async getGmailAuthUrl(): Promise<string> {
    const res = await apiClient.get<{ success: boolean; data: { url: string } }>(
      '/api/connectors/gmail/auth-url'
    );
    return res.data.data.url;
  },

  /** Gmail 연결 해제 */
  async disconnectGmail(): Promise<void> {
    await apiClient.delete('/api/connectors/gmail');
  },

  /** Notion 연결 테스트 + 토큰 저장 */
  async testNotionToken(token: string): Promise<{ workspaceName: string }> {
    const res = await apiClient.post<{ success: boolean; workspaceName: string; error?: string }>(
      '/api/connectors/notion/test',
      { token }
    );
    if (!res.data.success) throw new Error(res.data.error || '연결 실패');
    return { workspaceName: res.data.workspaceName };
  },

  /** Notion 설정 조회 */
  async getNotionConfig(): Promise<{ connected: boolean; workspaceName?: string } | null> {
    const res = await apiClient.get<{ success: boolean; data: { connected: boolean; workspaceName?: string } | null }>(
      '/api/connectors/notion/config'
    );
    return res.data.data;
  },

  /** Notion 연결 해제 */
  async disconnectNotion(): Promise<void> {
    await apiClient.delete('/api/connectors/notion');
  },

  // ── Slack ───────────────────────────────────────────────────────────────────

  /** Slack 연결 테스트 + Bot Token 저장 */
  async testSlackToken(token: string): Promise<{ team: string; botUser: string }> {
    const res = await apiClient.post<{ success: boolean; team: string; botUser: string; error?: string }>(
      '/api/connectors/slack/test',
      { token }
    );
    if (!res.data.success) throw new Error(res.data.error || '연결 실패');
    return { team: res.data.team, botUser: res.data.botUser };
  },

  /** Slack 채널 목록 조회 */
  async getSlackChannels(): Promise<Array<{ id: string; name: string; isPrivate: boolean }>> {
    const res = await apiClient.get<{ success: boolean; channels: Array<{ id: string; name: string; isPrivate: boolean }> }>(
      '/api/connectors/slack/channels'
    );
    return res.data.channels || [];
  },

  /** Slack 설정 조회 */
  async getSlackConfig(): Promise<{ connected: boolean; team?: string } | null> {
    const res = await apiClient.get<{ success: boolean; data: { connected: boolean; team?: string } | null }>(
      '/api/connectors/slack/config'
    );
    return res.data.data;
  },

  /** Slack 연결 해제 */
  async disconnectSlack(): Promise<void> {
    await apiClient.delete('/api/connectors/slack');
  },

  // ── Google Drive ──────────────────────────────────────────────────────────

  /** Google Drive Client ID/Secret 저장 */
  async saveGoogleDriveConfig(clientId: string, clientSecret: string): Promise<GmailConnectorConfig> {
    const res = await apiClient.post<{ success: boolean; data: GmailConnectorConfig }>(
      '/api/connectors/google-drive/config',
      { clientId, clientSecret }
    );
    return res.data.data;
  },

  /** Google Drive 설정 조회 */
  async getGoogleDriveConfig(): Promise<GmailConnectorConfig | null> {
    const res = await apiClient.get<{ success: boolean; data: GmailConnectorConfig | null }>(
      '/api/connectors/google-drive/config'
    );
    return res.data.data;
  },

  /** Google Drive OAuth 인증 URL 조회 */
  async getGoogleDriveAuthUrl(): Promise<string> {
    const res = await apiClient.get<{ success: boolean; data: { url: string } }>(
      '/api/connectors/google-drive/auth-url'
    );
    return res.data.data.url;
  },

  /** Google Drive 연결 해제 */
  async disconnectGoogleDrive(): Promise<void> {
    await apiClient.delete('/api/connectors/google-drive');
  },

  /** Google Drive 파일 목록 조회 */
  async listGoogleDriveFiles(folderId?: string, fileFilter?: string): Promise<Array<{
    id: string; name: string; mimeType: string; size?: number; modifiedTime?: string; webViewLink?: string;
  }>> {
    const res = await apiClient.post<{ success: boolean; data: Array<{
      id: string; name: string; mimeType: string; size?: number; modifiedTime?: string; webViewLink?: string;
    }> }>(
      '/api/connectors/google-drive/files',
      { folderId, fileFilter }
    );
    return res.data.data;
  },

  /** Google Drive 파일을 지식베이스로 가져오기 */
  async importDriveFileToKb(kbId: string, fileId: string, fileName: string): Promise<void> {
    await apiClient.post(
      `/api/connectors/google-drive/import-to-kb`,
      { kbId, fileId, fileName }
    );
  },
  async uploadToGoogleDrive(file: File, folderId?: string): Promise<{ id: string; name: string; webViewLink: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) formData.append('folderId', folderId);
    const res = await apiClient.post<{ success: boolean; data: { id: string; name: string; webViewLink: string } }>(
      '/api/connectors/google-drive/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data.data;
  },

  // ── 지식베이스용 Google Drive (워크플로우와 분리) ─────────────────────────────

  /** KB용 Google Drive Client ID/Secret 저장 */
  async saveKbGoogleDriveConfig(clientId: string, clientSecret: string): Promise<GmailConnectorConfig> {
    const res = await apiClient.post<{ success: boolean; data: GmailConnectorConfig }>(
      '/api/connectors/kb-google-drive/config',
      { clientId, clientSecret }
    );
    return res.data.data;
  },

  /** KB용 Google Drive 설정 조회 */
  async getKbGoogleDriveConfig(): Promise<GmailConnectorConfig | null> {
    const res = await apiClient.get<{ success: boolean; data: GmailConnectorConfig | null }>(
      '/api/connectors/kb-google-drive/config'
    );
    return res.data.data;
  },

  /** KB용 Google Drive OAuth 인증 URL 조회 */
  async getKbGoogleDriveAuthUrl(): Promise<string> {
    const res = await apiClient.get<{ success: boolean; data: { url: string } }>(
      '/api/connectors/kb-google-drive/auth-url'
    );
    return res.data.data.url;
  },

  /** KB용 Google Drive 연결 해제 */
  async disconnectKbGoogleDrive(): Promise<void> {
    await apiClient.delete('/api/connectors/kb-google-drive');
  },

  /** KB용 Google Drive 파일 목록 조회 */
  async listKbGoogleDriveFiles(folderId?: string, fileFilter?: string): Promise<Array<{
    id: string; name: string; mimeType: string; size?: number; modifiedTime?: string; webViewLink?: string;
  }>> {
    const res = await apiClient.post<{ success: boolean; data: Array<{
      id: string; name: string; mimeType: string; size?: number; modifiedTime?: string; webViewLink?: string;
    }> }>(
      '/api/connectors/kb-google-drive/files',
      { folderId, fileFilter }
    );
    return res.data.data;
  },

  /** KB용 Google Drive 파일을 지식베이스로 가져오기 */
  async importKbDriveFileToKb(kbId: string, fileId: string, fileName: string): Promise<void> {
    await apiClient.post(
      `/api/connectors/kb-google-drive/import-to-kb`,
      { kbId, fileId, fileName }
    );
  },

  // ── Dropbox (워크플로우 커넥터) ──────────────────────────────────────────────

  async saveDropboxConfig(clientId: string, clientSecret: string): Promise<GmailConnectorConfig> {
    const res = await apiClient.post<{ success: boolean; data: GmailConnectorConfig }>(
      '/api/connectors/dropbox/config', { clientId, clientSecret }
    );
    return res.data.data;
  },

  async getDropboxConfig(): Promise<GmailConnectorConfig | null> {
    const res = await apiClient.get<{ success: boolean; data: GmailConnectorConfig | null }>(
      '/api/connectors/dropbox/config'
    );
    return res.data.data;
  },

  async getDropboxAuthUrl(): Promise<string> {
    const res = await apiClient.get<{ success: boolean; data: { url: string } }>(
      '/api/connectors/dropbox/auth-url'
    );
    return res.data.data.url;
  },

  async disconnectDropbox(): Promise<void> {
    await apiClient.delete('/api/connectors/dropbox');
  },

  async listDropboxFiles(folderPath?: string, fileFilter?: string): Promise<Array<{
    id: string; name: string; path_lower: string; '.tag': string; size?: number; server_modified?: string;
  }>> {
    const res = await apiClient.post<{ success: boolean; data: Array<{
      id: string; name: string; path_lower: string; '.tag': string; size?: number; server_modified?: string;
    }> }>(
      '/api/connectors/dropbox/files', { folderPath, fileFilter }
    );
    return res.data.data;
  },

  async importDropboxFileToKb(kbId: string, filePath: string, fileName: string): Promise<void> {
    await apiClient.post('/api/connectors/dropbox/import-to-kb', { kbId, filePath, fileName });
  },

  async uploadToDropbox(file: File, folderPath?: string): Promise<{ id: string; name: string; path: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (folderPath) formData.append('folderPath', folderPath);
    const res = await apiClient.post<{ success: boolean; data: { id: string; name: string; path: string } }>(
      '/api/connectors/dropbox/upload', formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data.data;
  },

  // ── 지식베이스용 Dropbox (워크플로우와 분리) ──────────────────────────────────

  async saveKbDropboxConfig(clientId: string, clientSecret: string): Promise<GmailConnectorConfig> {
    const res = await apiClient.post<{ success: boolean; data: GmailConnectorConfig }>(
      '/api/connectors/kb-dropbox/config', { clientId, clientSecret }
    );
    return res.data.data;
  },

  async getKbDropboxConfig(): Promise<GmailConnectorConfig | null> {
    const res = await apiClient.get<{ success: boolean; data: GmailConnectorConfig | null }>(
      '/api/connectors/kb-dropbox/config'
    );
    return res.data.data;
  },

  async getKbDropboxAuthUrl(): Promise<string> {
    const res = await apiClient.get<{ success: boolean; data: { url: string } }>(
      '/api/connectors/kb-dropbox/auth-url'
    );
    return res.data.data.url;
  },

  async disconnectKbDropbox(): Promise<void> {
    await apiClient.delete('/api/connectors/kb-dropbox');
  },

  async listKbDropboxFiles(folderPath?: string, fileFilter?: string): Promise<Array<{
    id: string; name: string; path_lower: string; '.tag': string; size?: number; server_modified?: string;
  }>> {
    const res = await apiClient.post<{ success: boolean; data: Array<{
      id: string; name: string; path_lower: string; '.tag': string; size?: number; server_modified?: string;
    }> }>(
      '/api/connectors/kb-dropbox/files', { folderPath, fileFilter }
    );
    return res.data.data;
  },

  async importKbDropboxFileToKb(kbId: string, filePath: string, fileName: string): Promise<void> {
    await apiClient.post('/api/connectors/kb-dropbox/import-to-kb', { kbId, filePath, fileName });
  },

  // ── 네이버 메일 ──────────────────────────────────────────────────────────

  /** 네이버 메일 연결 테스트 + 저장 */
  async testNaverMail(email: string, appPassword: string): Promise<{ email: string }> {
    try {
      const res = await apiClient.post<{ success: boolean; email: string; error?: string }>(
        '/api/connectors/naver-mail/test',
        { email, appPassword }
      );
      if (!res.data.success) throw new Error(res.data.error || '연결 실패');
      return { email: res.data.email };
    } catch (err: unknown) {
      // axios 에러에서 서버 응답 메시지 추출
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      const serverMsg = axiosErr?.response?.data?.error;
      throw new Error(serverMsg || axiosErr?.message || '연결 실패');
    }
  },

  /** 네이버 메일 설정 조회 */
  async getNaverMailConfig(): Promise<GmailConnectorConfig | null> {
    const res = await apiClient.get<{ success: boolean; data: GmailConnectorConfig | null }>(
      '/api/connectors/naver-mail/config'
    );
    return res.data.data;
  },

  /** 네이버 메일 연결 해제 */
  async disconnectNaverMail(): Promise<void> {
    await apiClient.delete('/api/connectors/naver-mail');
  },

  // ── 웹 검색 (Tavily / Serper / Brave) ─────────────────────────────────────

  /** 웹 검색 연결 테스트 + 저장 */
  async testWebSearch(provider: 'tavily' | 'serper' | 'brave' | 'you', apiKey: string): Promise<void> {
    try {
      const res = await apiClient.post<{ success: boolean; error?: string }>(
        '/api/connectors/web-search/test',
        { provider, apiKey }
      );
      if (!res.data.success) throw new Error(res.data.error || '연결 실패');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      throw new Error(axiosErr?.response?.data?.error || axiosErr?.message || '연결 실패');
    }
  },

  /** 웹 검색 설정 조회 */
  async getWebSearchConfig(): Promise<{ connected: boolean; provider?: string; apiKey?: string } | null> {
    const res = await apiClient.get<{ success: boolean; data: { connected: boolean; provider?: string; apiKey?: string } | null }>(
      '/api/connectors/web-search/config'
    );
    return res.data.data;
  },

  /** 웹 검색 연결 해제 */
  async disconnectWebSearch(): Promise<void> {
    await apiClient.delete('/api/connectors/web-search');
  },

  // ── MCP 서버 ──────────────────────────────────────────────────────────────

  /** MCP 서버 툴 목록 조회 */
  async listMcpTools(url: string, apiKey?: string): Promise<Array<{
    name: string;
    description?: string;
    inputSchema?: Record<string, unknown>;
  }>> {
    try {
      const res = await apiClient.post<{
        success: boolean;
        needsOAuth?: boolean;
        authorizationEndpoint?: string;
        tokenEndpoint?: string;
        registrationEndpoint?: string;
        data?: Array<{ name: string; description?: string; inputSchema?: Record<string, unknown> }>;
      }>('/api/connectors/mcp/tools', { url, apiKey }, { validateStatus: (s) => s < 500 });

      if (res.data.needsOAuth) {
        // OAuth 필요 — 인증 URL 생성 요청
        const authRes = await apiClient.post<{ success: boolean; data: { url: string } }>(
          '/api/connectors/mcp/oauth/auth-url',
          {
            url,
            authorizationEndpoint: res.data.authorizationEndpoint,
            tokenEndpoint: res.data.tokenEndpoint,
            registrationEndpoint: res.data.registrationEndpoint,
          }
        );
        const authUrl = authRes.data.data.url;
        // 팝업 열기
        const popup = window.open(authUrl, 'mcp-auth', 'width=500,height=700');
        
        // 팝업이 닫힐 때까지 대기 후 상태 재조회
        return new Promise((resolve, reject) => {
          const timer = setInterval(async () => {
            if (!popup?.closed) return;
            clearInterval(timer);
            
            // 팝업 닫힘 → OAuth 완료됐는지 재조회로 확인
            try {
              const tools = await connectorApi.fetchMcpTools(url);
              resolve(tools);
            } catch {
              reject(new Error('MCP 서버 연결에 실패했습니다. 다시 시도해주세요.'));
            }
          }, 300);
        });
      }

      return res.data.data ?? [];
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      throw new Error(axiosErr?.response?.data?.error || axiosErr?.message || 'MCP 서버 연결 실패');
    }
  },

  /**
   * MCP 툴 목록 순수 조회 (OAuth 팝업 분기 없음)
   * - OAuth 인증 성공 후 저장된 토큰으로 재조회할 때 사용한다.
   * - needsOAuth 가 다시 떠도 팝업을 열지 않고 에러로 처리하여 무한 루프를 방지한다.
   */
  async fetchMcpTools(url: string): Promise<Array<{
    name: string;
    description?: string;
    inputSchema?: Record<string, unknown>;
  }>> {
    const res = await apiClient.post<{
      success: boolean;
      needsOAuth?: boolean;
      data?: Array<{ name: string; description?: string; inputSchema?: Record<string, unknown> }>;
    }>('/api/connectors/mcp/tools', { url }, { validateStatus: (s) => s < 500 });

    if (res.data.needsOAuth) {
      throw new Error('인증 후에도 MCP 서버 연결에 실패했습니다. 다시 시도해주세요.');
    }
    return res.data.data ?? [];
  },

  /** MCP 툴 실행 */
  async callMcpTool(url: string, toolName: string, toolArgs?: Record<string, unknown>, apiKey?: string): Promise<unknown> {
    const res = await apiClient.post<{ success: boolean; data: unknown }>(
      '/api/connectors/mcp/call',
      { url, apiKey, toolName, toolArgs }
    );
    return res.data.data;
  },
};

export default connectorApi;
